import { EVENT_TYPE } from '@atlaskit/editor-common/analytics';
import { type EditorState } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	ADFStreamer,
	type FireAnalyticEventCallback as ADFStreamerFireAnalyticEventCallback,
} from '@atlassian/ai-model-io/adf-streamer';
import type { ContentStatistics } from '@atlassian/ai-model-io/utils/prosemirror-content-statistics';

import type { ADFStreamingErrorAEP, ADFStreamingInfoAEP } from '../../../types/analytics';
import type { SelectionType } from '../../prebuilt/config-items/config-items';
import type { SELECTION_CATEGORY } from '../analytics/enums';
import { type FireAIAnalyticsEventCallback } from '../analytics/utils';

type CreateAnalyticsEventOptions = {
	aiInteractionID?: string;
	aiSessionId?: string;
	contentStatistics?: ContentStatistics;
	experienceName: string;
	featureId?: string;
	fireAIAnalyticsEvent: FireAIAnalyticsEventCallback;
	retryAttemptNumber?: number;
	selectionCategory: SELECTION_CATEGORY;
	selectionType: SelectionType;
	streamId: string;
	traceId?: string;
};

const createFireADFStreamingAnalyticsEvent =
	({
		fireAIAnalyticsEvent,
		experienceName,
		featureId,
		aiSessionId,
		aiInteractionID,
		selectionType,
		selectionCategory,
		contentStatistics,
		streamId,
		retryAttemptNumber,
		traceId,
	}: CreateAnalyticsEventOptions): ADFStreamerFireAnalyticEventCallback =>
	(event) => {
		if (fg('platform_editor_ai_traceid_in_analytics_events')) {
			const commonAttributes:
				| Omit<ADFStreamingErrorAEP['attributes'], 'errorMessage' | 'errorData'>
				| Omit<ADFStreamingInfoAEP['attributes'], 'message' | 'data'> = {
				experienceName,
				featureId,
				aiSessionId,
				aiInteractionID,
				selectionType,
				selectionCategory,
				contentStatistics,
				streamId,
				retryAttemptNumber,
				traceId,
				source: { type: 'plugin' },
			};
			if (event.type === 'error') {
				fireAIAnalyticsEvent({
					payload: {
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							errorMessage: event.message,
							errorData: event.data,
						},
					},
				});
			} else {
				fireAIAnalyticsEvent({
					payload: {
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							...commonAttributes,
							message: event.message,
							data: event.data,
						},
					},
				});
			}
		} else {
			if (event.type === 'error') {
				fireAIAnalyticsEvent({
					payload: {
						action: 'error',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							errorMessage: event.message,
							errorData: event.data,
							experienceName,
							featureId,
							aiSessionId,
							aiInteractionID,
							selectionType,
							selectionCategory,
							contentStatistics,
							streamId,
							retryAttemptNumber,
							source: { type: 'plugin' },
						},
					},
				});
			} else {
				fireAIAnalyticsEvent({
					payload: {
						action: 'info',
						actionSubject: 'editorPluginAI',
						actionSubjectId: 'adfStreaming',
						eventType: EVENT_TYPE.OPERATIONAL,
						attributes: {
							message: event.message,
							data: event.data,
							experienceName,
							featureId,
							aiSessionId,
							aiInteractionID,
							selectionType,
							selectionCategory,
							contentStatistics,
							streamId,
							retryAttemptNumber,
							source: { type: 'plugin' },
						},
					},
				});
			}
		}
	};

type CreateADFStreamerOptions = CreateAnalyticsEventOptions & {
	state: EditorState;
};
/**
 * Creates an ADF streamer instance configured with the editor state schema.
 *
 * @param options - The options for creating the ADF streamer.
 * @returns The configured ADF streamer instance.
 *
 * @example
 * const streamer = createADFStreamer(state);
 * streamer.processChunk(chunk);
 */
export function createADFStreamer(options: CreateADFStreamerOptions) {
	return new ADFStreamer(
		options.state.schema,
		createFireADFStreamingAnalyticsEvent({
			fireAIAnalyticsEvent: options.fireAIAnalyticsEvent,
			experienceName: options.experienceName,
			featureId: options.featureId,
			aiSessionId: options.aiSessionId,
			aiInteractionID: options.aiInteractionID,
			selectionType: options.selectionType,
			contentStatistics: options.contentStatistics,
			selectionCategory: options.selectionCategory,
			streamId: options.streamId,
			retryAttemptNumber: options.retryAttemptNumber,
			traceId: fg('platform_editor_ai_traceid_in_analytics_events') ? options.traceId : undefined,
		}),
	);
}
